import type { ReactNode } from 'react'
import { AlertTriangle, Inbox, RotateCw } from 'lucide-react'

import type { ApiErrorBody } from '@cattle/contracts'

import { cn } from '../lib/cn'
import { t } from '../lib/i18n'

import { Button, Card } from './primitives'

/* ── LoadingSkeleton ──────────────────────────────────────────────────────
   A skeleton in the SHAPE of the answer, never a spinner on a blank page. The
   reader should be able to tell what is arriving before it arrives.
   ────────────────────────────────────────────────────────────────────────── */

export function Skeleton({ className }: { className?: string }) {
  return <div className={cn('animate-pulse rounded-lg bg-muted/20', className)} aria-hidden="true" />
}

export function LoadingSkeleton({
  variant = 'table',
  rows = 6,
  tiles = 4,
  className,
}: {
  variant?: 'table' | 'tiles' | 'panel' | 'list'
  rows?: number
  tiles?: number
  className?: string
}) {
  const label = t('state.loading')

  if (variant === 'tiles') {
    return (
      <div className={cn('grid gap-3.5 sm:grid-cols-2 xl:grid-cols-4', className)} aria-label={label} aria-busy="true">
        {Array.from({ length: tiles }, (_, index) => (
          <Card key={index} padded={false} className="px-[17px] py-4">
            <Skeleton className="h-2.5 w-20" />
            <Skeleton className="mt-3 h-7 w-32" />
            <Skeleton className="mt-2 h-2.5 w-24" />
          </Card>
        ))}
      </div>
    )
  }

  if (variant === 'panel') {
    return (
      <Card surface="heavy" className={className} aria-label={label} aria-busy="true">
        <Skeleton className="h-4 w-40" />
        <Skeleton className="mt-4 h-3 w-full" />
        <Skeleton className="mt-2.5 h-3 w-5/6" />
        <Skeleton className="mt-2.5 h-3 w-4/6" />
      </Card>
    )
  }

  if (variant === 'list') {
    return (
      <div className={cn('flex flex-col gap-3', className)} aria-label={label} aria-busy="true">
        {Array.from({ length: rows }, (_, index) => (
          <div key={index} className="flex items-center gap-3">
            <Skeleton className="size-[30px] rounded-[10px]" />
            <div className="flex-1">
              <Skeleton className="h-3 w-40" />
              <Skeleton className="mt-2 h-2.5 w-24" />
            </div>
            <Skeleton className="h-3 w-16" />
          </div>
        ))}
      </div>
    )
  }

  return (
    <div className={className} aria-label={label} aria-busy="true">
      <div className="flex gap-4 border-b border-muted/25 pb-3">
        {Array.from({ length: 5 }, (_, index) => (
          <Skeleton key={index} className="h-2.5 flex-1" />
        ))}
      </div>
      {Array.from({ length: rows }, (_, index) => (
        <div key={index} className="flex items-center gap-4 border-b border-muted/12 py-3.5">
          <Skeleton className="size-[30px] shrink-0 rounded-[10px]" />
          {Array.from({ length: 4 }, (_, cell) => (
            <Skeleton key={cell} className="h-3 flex-1" />
          ))}
        </div>
      ))}
    </div>
  )
}

/* ── EmptyState — illustration, one line, and the primary action ──────────── */

export function EmptyState({
  icon,
  title,
  body,
  action,
  className,
}: {
  icon?: ReactNode
  title: string
  body: string
  action?: ReactNode
  className?: string
}) {
  return (
    <div className={cn('flex flex-col items-center px-6 py-14 text-center', className)}>
      <div
        className="flex size-14 items-center justify-center rounded-full border border-accent-glass-border bg-accent-glass text-accent-dark"
        aria-hidden="true"
      >
        {icon ?? <Inbox className="size-6" />}
      </div>
      <h3 className="mt-3.5 font-display text-lg font-bold text-dark">{title}</h3>
      <p className="mt-1.5 max-w-[42ch] text-[13.5px] leading-relaxed text-sub">{body}</p>
      {action ? <div className="mt-4">{action}</div> : null}
    </div>
  )
}

/* ── ErrorState ───────────────────────────────────────────────────────────
   The `requestId` is shown deliberately. It is echoed in the response, the
   `X-Request-Id` header and the server log line, so a support ticket quoting
   one id resolves to exactly one request.
   ────────────────────────────────────────────────────────────────────────── */

export function ErrorState({
  error,
  onRetry,
  className,
}: {
  error: ApiErrorBody
  onRetry?: () => void
  className?: string
}) {
  return (
    <div className={cn('flex flex-col items-center px-6 py-14 text-center', className)} role="alert">
      <div
        className="flex size-14 items-center justify-center rounded-full border border-danger/25 bg-danger/10 text-danger"
        aria-hidden="true"
      >
        <AlertTriangle className="size-6" />
      </div>
      <h3 className="mt-3.5 font-display text-lg font-bold text-dark">{t('state.errorTitle')}</h3>
      <p className="mt-1.5 max-w-[42ch] text-[13.5px] leading-relaxed text-sub">{error.message}</p>
      {/* A transport failure never reached a server, so no id was ever
          assigned. Printing an empty one invites a support ticket quoting
          nothing. */}
      {error.requestId ? (
        <p className="mt-2 font-display text-xs tnum text-muted">
          {t('state.requestId', { requestId: error.requestId })}
        </p>
      ) : null}
      {onRetry ? (
        <Button className="mt-4" icon={<RotateCw className="size-4" />} onClick={onRetry}>
          {t('act.retry')}
        </Button>
      ) : null}
    </div>
  )
}

/* ── ForbiddenState ───────────────────────────────────────────────────────
   Reached only by typing a URL. The route is ABSENT from navigation for a role
   that cannot use it — a greyed-out item still tells them what exists.
   ────────────────────────────────────────────────────────────────────────── */

export function ForbiddenState({ className }: { className?: string }) {
  return (
    <EmptyState
      className={className}
      title={t('state.forbiddenTitle')}
      body={t('state.forbiddenBody')}
    />
  )
}
